import React, { useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-reduced-motion";

interface Position {
  x: number;
  y: number;
}

interface SpotlightCardProps
  extends
    React.PropsWithChildren,
    Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "onMouseMove" | "onMouseEnter" | "onMouseLeave" | "onFocus" | "onBlur"
    > {
  className?: string;
  spotlightColor?: `rgba(${number}, ${number}, ${number}, ${number})`;
}

const SpotlightCard: React.FC<SpotlightCardProps> = ({
  children,
  className = "",
  spotlightColor = "rgba(255, 255, 255, 0.25)",
  ...rest
}) => {
  const reduced = usePrefersReducedMotion();
  const divRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState<number>(0);
  const effectiveOpacity = reduced ? 0 : opacity;

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (isFocused) return;

    const rect = rectRef.current;
    if (!rect) return;
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => {
    setIsFocused(true);
    setOpacity(0.6);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setOpacity(0);
  };

  const handleMouseEnter = () => {
    rectRef.current = divRef.current?.getBoundingClientRect() ?? null;
    setOpacity(0.6);
  };

  const handleMouseLeave = () => {
    rectRef.current = null;
    setOpacity(0);
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative rounded-3xl border border-neutral-800 bg-neutral-900 overflow-hidden p-8 ${className}`}
      {...rest}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out"
        style={{
          opacity: effectiveOpacity,
          background: `radial-gradient(circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 80%)`,
        }}
      />
      {children}
    </div>
  );
};

export default SpotlightCard;
